import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  ExportIcon,
  FilterIcon,
  HeldInReserveIcon,
  NavCloseIcon,
  NoSearchResultsIcon,
  SearchIconWithBorder
} from 'assets/icons';
import { CustomButton, InfiniteLoadingSpinner, NoPermissions, SearchBar, SidePanel } from 'components';
import PayoutTab from 'containers/payouts/components/PayoutTab';
import PayoutTabGroup from 'containers/payouts/components/PayoutTabGroup';
import {
  HELD_IN_RESERVE_DATE_OPTIONS,
  HeldInReserveStatus,
  PAYOUT_TXNS_AMOUNT_FILTERS,
  PAYOUT_TXNS_HELD_IN_RESERVE_TABS,
  PAYOUT_TXNS_PAGES
} from 'containers/payouts/constants';
import routesPath from 'routes/RoutesPath';
import { useNavigate } from 'react-router-dom';
import { usePaginatedReservedFundsQuery } from 'containers/payouts/hooks/usePaginatedReservedFundsQuery';
import HoldAndReleaseCard from './HoldAndReleaseCard';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import HeldInReserveFilters from './HeldInReserveFilters';
import debounce from 'lodash/debounce';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { selectCurrentTeam, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  initialHeldInReservePageState,
  selectHeldInReserveFilters,
  setHeldInReserveFilters
} from 'containers/payouts/payoutsSlice';
import { getAmountFilterString } from 'containers/payouts/utils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { useLazyDownloadReservedFundsQuery } from 'containers/payouts/api';
import { AmountFilterValue } from 'containers/payouts/payouts.model';
import { DOWNLOAD_NOTIFIER_TOAST } from 'constants/common';

interface HeldInReservePanelProps {
  className?: string;
}

const HeldInReservePanel: FC<HeldInReservePanelProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const heldInReserveFilters = useAppSelector(selectHeldInReserveFilters);

  const [appliedAmountFilter, setAppliedAmountFilter] = useState<AmountFilterValue>({
    title: PAYOUT_TXNS_AMOUNT_FILTERS.ALL,
    min: '',
    max: ''
  });
  const [appliedCreatedDateFilter, setAppliedCreatedDateFilter] = useState({
    option: HELD_IN_RESERVE_DATE_OPTIONS.CREATED.ALL,
    startDate: '',
    endDate: ''
  });
  const [appliedAvailableDateFilter, setAppliedAvailableDateFilter] = useState({
    option: HELD_IN_RESERVE_DATE_OPTIONS.AVAILABLE.ALL,
    startDate: '',
    endDate: ''
  });
  const [appliedStatus, setAppliedStatus] = useState<string>(HeldInReserveStatus.HELD);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<PAYOUT_TXNS_PAGES>(PAYOUT_TXNS_PAGES.HELD_IN_RESERVE);
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<PAYOUT_TXNS_HELD_IN_RESERVE_TABS>(
    PAYOUT_TXNS_HELD_IN_RESERVE_TABS.TO_BE_RELEASED
  );

  const initialMount = useRef(true);

  const [downloadReservedFunds] = useLazyDownloadReservedFundsQuery();

  const {
    isLoading,
    isFetching,
    groupedList,
    totalCount,
    totalAmount,
    loadMore,
    hasMore,
    isLoadingSuccess,
    hasPermissionsToView,
    setQueryParams,
    queryParams
  } = usePaginatedReservedFundsQuery();

  const reservedFundsLoader = useRef(loadMore);

  const { setIntersectionElement } = useIntersectionObserver({
    intersectionCallBack: reservedFundsLoader,
    threshold: 0.5
  });

  useEffect(() => {
    reservedFundsLoader.current = loadMore;
  }, [loadMore]);

  useEffect(() => {
    if (initialMount.current) {
      setAppliedAmountFilter(heldInReserveFilters.amount);
      setAppliedCreatedDateFilter(heldInReserveFilters.createdDateFilter);
      setAppliedAvailableDateFilter(heldInReserveFilters.availableDateFilter);
      setSelectedTab(heldInReserveFilters.tab);
      setAppliedStatus(heldInReserveFilters.status);
      setSearchText(heldInReserveFilters.transactionId);
      initialMount.current = false;
    }
  }, [heldInReserveFilters]);

  useEffect(() => {
    setQueryParams({
      status: appliedStatus === HeldInReserveStatus.ALL ? null : appliedStatus?.toUpperCase(),
      createdEnd: appliedCreatedDateFilter.endDate || null,
      createdStart: appliedCreatedDateFilter.startDate || null,
      availableEnd: appliedAvailableDateFilter.endDate || null,
      availableStart: appliedAvailableDateFilter.startDate || null,
      amount: getAmountFilterString(appliedAmountFilter) || null,
      transactionId: searchText || null
    });
  }, [appliedStatus, appliedCreatedDateFilter, appliedAvailableDateFilter, appliedAmountFilter, searchText]);

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const onSearchTermChange = (text: string) => {
    setSearchText(text);
    debouncedSearchHandler(text);
  };

  const initiateSearch = (searchTerm: string) => {
    dispatch(setHeldInReserveFilters({ ...heldInReserveFilters, transactionId: searchTerm }));
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [queryParams]);

  const onClearSearch = () => {
    setSearchText('');
    setIsSearchExpanded(false);
    initiateSearch('');
  };

  const changeTabs = (tab: PAYOUT_TXNS_HELD_IN_RESERVE_TABS) => {
    if (tab === selectedTab) return;
    setSelectedTab(tab);
    setAppliedStatus(tab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL ? HeldInReserveStatus.ALL : HeldInReserveStatus.HELD);
  };

  const handleSidePanelClose = () => {
    navigate(routesPath.PAYOUTS_BALANCE);
    dispatch(setHeldInReserveFilters(initialHeldInReservePageState));
  };

  const handleFilterClick = () => {
    setPage(PAYOUT_TXNS_PAGES.FILTERS);
  };

  const handleFilterClose = () => {
    setPage(PAYOUT_TXNS_PAGES.HELD_IN_RESERVE);
  };

  const handleApplyFilters = filters => {
    setAppliedAmountFilter(filters.amount);
    setAppliedCreatedDateFilter(filters.createdDateFilter);
    setAppliedAvailableDateFilter(filters.availableDateFilter);
    setAppliedStatus(filters.status);
    dispatch(setHeldInReserveFilters({ ...heldInReserveFilters, ...filters }));
    setPage(PAYOUT_TXNS_PAGES.HELD_IN_RESERVE);
  };

  const clearFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAppliedAmountFilter({ title: '', min: '', max: '' });
    setAppliedCreatedDateFilter({ option: HELD_IN_RESERVE_DATE_OPTIONS.CREATED.ALL, startDate: '', endDate: '' });
    setAppliedAvailableDateFilter({ option: HELD_IN_RESERVE_DATE_OPTIONS.AVAILABLE.ALL, startDate: '', endDate: '' });
    setAppliedStatus(
      selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL ? HeldInReserveStatus.ALL : HeldInReserveStatus.HELD
    );
    dispatch(setHeldInReserveFilters(initialHeldInReservePageState));
  };

  const getFilterCount = () => {
    let count = 0;
    if (appliedAmountFilter.title && appliedAmountFilter.title !== PAYOUT_TXNS_AMOUNT_FILTERS.ALL) {
      count += 1;
    }
    if (
      appliedCreatedDateFilter.option &&
      appliedCreatedDateFilter.option !== HELD_IN_RESERVE_DATE_OPTIONS.CREATED.ALL
    ) {
      count += 1;
    }
    if (
      appliedAvailableDateFilter.option &&
      appliedAvailableDateFilter.option !== HELD_IN_RESERVE_DATE_OPTIONS.AVAILABLE.ALL
    ) {
      count += 1;
    }
    if (
      appliedStatus &&
      appliedStatus !== HeldInReserveStatus.ALL &&
      selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL
    ) {
      count += 1;
    }
    return count;
  };

  const onExportClick = () => {
    if (currentTeam?.id) {
      dispatch(showNotifier(DOWNLOAD_NOTIFIER_TOAST));
      downloadReservedFunds({
        teamId: currentTeam.id,
        page: 0,
        limit: 0,
        status: appliedStatus === HeldInReserveStatus.ALL ? null : appliedStatus?.toUpperCase(),
        createdStart: appliedCreatedDateFilter.startDate || null,
        createdEnd: appliedCreatedDateFilter.endDate || null,
        availableStart: appliedAvailableDateFilter.startDate || null,
        availableEnd: appliedAvailableDateFilter.endDate || null,
        amount: getAmountFilterString(appliedAmountFilter) || null,
        transactionId: searchText || null
      });
    }
  };

  const handleViewPaymentClick = (txnId: string) => {
    navigate(`/payouts/balance/${currentTeam?.id}/balance/receipt/${txnId}?type=CA`, {
      state: FROM_SIDE_PANEL_ROUTE_STATE
    });
  };

  const showHeldInReserveTile =
    !isFetching &&
    isLoadingSuccess &&
    selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.TO_BE_RELEASED &&
    +totalAmount > 0 &&
    +totalCount > 0 &&
    getFilterCount() === 0 &&
    !searchText;

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose}>
      {page === PAYOUT_TXNS_PAGES.FILTERS && (
        <HeldInReserveFilters
          handleClose={handleFilterClose}
          onApply={handleApplyFilters}
          appliedAmountFilter={appliedAmountFilter}
          selectedTab={selectedTab}
          appliedCreatedDateFilter={appliedCreatedDateFilter}
          appliedAvailableDateFilter={appliedAvailableDateFilter}
          appliedStatus={appliedStatus}
        />
      )}
      {page === PAYOUT_TXNS_PAGES.HELD_IN_RESERVE && (
        <div className="customNormalScroll h-full w-96 overflow-auto py-5">
          <div className="flex items-center gap-2 px-3.5">
            <div onClick={handleSidePanelClose} className="cursor-pointer">
              <NavCloseIcon />
            </div>
            <div className="text-17px font-semibold text-headingGray">Reserved funds</div>
          </div>
          {hasPermissionsToView ? (
            <div className="overflow-auto">
              <PayoutTabGroup className="mx-4 my-5">
                <PayoutTab
                  label={PAYOUT_TXNS_HELD_IN_RESERVE_TABS.TO_BE_RELEASED}
                  isActive={selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.TO_BE_RELEASED}
                  count={totalCount}
                  onClick={() => changeTabs(PAYOUT_TXNS_HELD_IN_RESERVE_TABS.TO_BE_RELEASED)}
                  showCount={selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.TO_BE_RELEASED}
                  isLoadingSuccess={isLoadingSuccess}
                  isLoadingData={isFetching}
                />
                <PayoutTab
                  label={PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL}
                  isActive={selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL}
                  count={totalCount}
                  onClick={() => changeTabs(PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL)}
                  showCount={selectedTab === PAYOUT_TXNS_HELD_IN_RESERVE_TABS.ALL}
                  isLoadingSuccess={isLoadingSuccess}
                  isLoadingData={isFetching}
                />
              </PayoutTabGroup>
              <>
                <div className="mx-4 mb-4 flex flex-wrap justify-between gap-2.5">
                  {isSearchExpanded || searchText ? (
                    <SearchBar
                      id="payouts-txns-search"
                      handleClear={onClearSearch}
                      handleChange={onSearchTermChange}
                      value={searchText}
                      placeholder="Search with Receipt id"
                      wrapperStyle={'w-full h-10 border border-borderGray rounded'}
                      showSearchIcon={true}
                      autoFocus={false}
                      showCrossIcon={true}
                    />
                  ) : (
                    <div onClick={handleSearchIconClick}>
                      <SearchIconWithBorder className="flex h-9 cursor-pointer" />
                    </div>
                  )}
                  <CustomButton
                    onClick={handleFilterClick}
                    id="held-in-reserve-filter-button"
                    StartIcon={FilterIcon}
                    type={null}
                    className="h-9 border border-borderGray bg-primary p-2.5 text-sbase font-normal text-tertiaryText">
                    <div className="flex items-center">
                      Filter
                      {getFilterCount() > 0 && (
                        <>
                          <span
                            className="ml-1 rounded-[10px] bg-[#F7C0C0] 
                            px-2.5 text-px13 font-semibold text-primaryText">
                            {getFilterCount()}
                          </span>
                          <span className="ml-2 cursor-pointer" onClick={clearFilters}>
                            <NavCloseIcon width={10} height={10} />
                          </span>
                        </>
                      )}
                    </div>
                  </CustomButton>
                  <CustomButton
                    onClick={onExportClick}
                    id="payout-txn-export-button"
                    StartIcon={ExportIcon}
                    type={null}
                    className="ml-auto h-9 border border-borderGray
                     bg-primary p-2.5 text-sbase font-normal text-tertiaryText">
                    Export
                  </CustomButton>
                </div>
                <div>
                  {(isLoading || isFetching) && (
                    <div className="pt-2">
                      <InfiniteLoadingSpinner />
                    </div>
                  )}
                  {showHeldInReserveTile && (
                    <div className="mx-4 mb-4 flex items-center gap-2 rounded-md border border-borderGray px-4 py-2">
                      <div className="flex h-10 w-10 shrink-0 items-center rounded-full bg-secondaryBtn p-2">
                        <HeldInReserveIcon />
                      </div>
                      <div className="text-17px font-semibold text-primaryText">Held in reserve</div>
                      <div className="ml-auto text-17px font-semibold text-primaryText">
                        {getAmountWithCurrency(totalAmount)}
                      </div>
                    </div>
                  )}
                  {Object.entries(groupedList).map(([date, funds]) => (
                    <div key={date}>
                      <div className="bg-secondaryBtn py-2.5 px-4 text-sbase text-accentText">{date}</div>
                      {funds.map(fund => (
                        <div key={fund.id} className="mx-4 border-b border-borderGray py-4">
                          <HoldAndReleaseCard reservedFund={fund} onViewPaymentClick={handleViewPaymentClick} />
                        </div>
                      ))}
                    </div>
                  ))}
                  {hasMore && !isLoading && !isFetching && <div ref={setIntersectionElement} className="h-2" />}
                  {isLoadingSuccess && !isFetching && totalCount === 0 && (
                    <div className="mt-40 flex h-full flex-col items-center justify-center gap-4 text-center">
                      <NoSearchResultsIcon height={24} width={24} />
                      <div className="text-sbase text-primaryText">
                        <div className="font-bold ">No transactions to show</div>
                        Try changing the filters or switch tabs.
                      </div>
                    </div>
                  )}
                </div>
              </>
            </div>
          ) : (
            <NoPermissions />
          )}
        </div>
      )}
    </SidePanel>
  );
};

export default HeldInReservePanel;
